import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAmplitude } from "@crehana/analytics";
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated

import { customLocalStorage, parseQueryStrings } from "@crehana/utils";
import ClientSideComponent from "../../ClientSideComponent";
import AuthDialog from "./AuthDialog";
import { views } from "./constants";
import useAuthDialogTranslation from "./hooks/useAuthDialogTranslation";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var AuthDialogContext = /*#__PURE__*/createContext(undefined);
export var AuthDialogProvider = function AuthDialogProvider(_ref) {
  var children = _ref.children,
    showCustomAffiliateAside = _ref.showCustomAffiliateAside,
    showNameFields = _ref.showNameFields;
  var _useAmplitude = useAmplitude(),
    logEvent = _useAmplitude.logEvent;
  var _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    authDialogProps = _useState2[0],
    setAuthDialogProps = _useState2[1];
  var _useState3 = useState({
      currentForm: views.REGISTER_VIEW,
      showModal: false,
      loginRegisterModalMounted: false,
      customMessages: {}
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    formState = _useState4[0],
    setFormState = _useState4[1];
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    loginRegisterSwitcherProps = _useState6[0],
    setLoginRegisterSwitcherProps = _useState6[1];
  var _useAuthDialogTransla = useAuthDialogTranslation(),
    t = _useAuthDialogTransla.t;
  var setNextUrl = function setNextUrl(nextUrl) {
    if (nextUrl) {
      window.nextUrl = nextUrl;
    }
  };
  var showLoginModal = function showLoginModal() {
    var nextUrl = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    var dialogProps = arguments.length > 1 ? arguments[1] : undefined;
    if (typeof nextUrl === 'string') {
      setNextUrl(nextUrl);
    }
    if (dialogProps) {
      setAuthDialogProps(dialogProps);
    }
    setFormState({
      currentForm: views.LOGIN_VIEW,
      showModal: true,
      loginRegisterModalMounted: true
    });
  };
  var showRegisterModal = function showRegisterModal() {
    var nextUrl = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    var dialogProps = arguments.length > 1 ? arguments[1] : undefined;
    var additionalLoginRegisterSwitcherProps = arguments.length > 2 ? arguments[2] : undefined;
    var originComponent = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'Unknow component';
    logEvent({
      event: 'Render Signup',
      data: {
        source_page: window.location.href,
        source_detail: originComponent,
        Type: 'Popup'
      }
    });
    if (typeof nextUrl === 'string') {
      setNextUrl(nextUrl);
    }
    if (additionalLoginRegisterSwitcherProps) {
      setLoginRegisterSwitcherProps(function (prevState) {
        return _objectSpread(_objectSpread({}, prevState), additionalLoginRegisterSwitcherProps);
      });
    }
    if (dialogProps) {
      setAuthDialogProps(dialogProps);
    }
    setFormState(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        currentForm: views.REGISTER_VIEW,
        showModal: true,
        loginRegisterModalMounted: true
      });
    });
  };
  var closeAuthenticateModal = function closeAuthenticateModal() {
    // temporal hack to don't subscribe users to temporal-courses if the auth dialog is closed
    // owners: Nexus and Wai Ka
    customLocalStorage.removeItem('courseId');
    setFormState(_objectSpread(_objectSpread({}, formState), {}, {
      showModal: false
    }));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    // @ts-expect-error This is just for compatibility
    window.showLoginModal = showLoginModal;
    // @ts-expect-error This is just for compatibility
    window.showRegisterModal = showRegisterModal;
    // @ts-expect-error This is just for compatibility
    window.closeAuthenticateModal = closeAuthenticateModal;

    /**
     * This feature is used in pages where We want to force the user to sign-in/up to access to the content
     * Ex: /curso-gratis-del-dia/?promo=true
     */
    var qs = parseQueryStrings(window.location.search);
    if (qs !== null && qs !== void 0 && qs.promo && (qs.promo === 'true' || qs.promo === true)) {
      setAuthDialogProps({
        hideCloseBtn: true,
        // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional empty block
        onDismiss: function onDismiss() {}
      });
      showRegisterModal(window.location.pathname);
      setFormState(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          customMessages: {
            registerTitle: t('AUTH_DIALOG_MOBILE_HEADER_TITLE_PROMO'),
            registerMessage: t('AUTH_DIALOG_MOBILE_HEADER_SUBTITLE_PROMO')
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var preloadAuthDialog = function preloadAuthDialog() {
    import(/* webpackChunkName: "login-register-modal" */"./LoginRegisterSwitcherDialog")["catch"](function () {
      return console.error('Error preloading login/register modal');
    });
  };
  var memoizedChildren = useMemo(function () {
    return children;
  }, [children]);
  return /*#__PURE__*/_jsxs(AuthDialogContext.Provider, {
    value: {
      showLoginModal: showLoginModal,
      showRegisterModal: showRegisterModal,
      closeAuthenticateModal: closeAuthenticateModal,
      preloadAuthDialog: preloadAuthDialog
    },
    children: [memoizedChildren, /*#__PURE__*/_jsx(ClientSideComponent, {
      children: /*#__PURE__*/_jsx(AuthDialog, {
        dialogProps: authDialogProps,
        formState: formState,
        loginRegisterSwitcherProps: loginRegisterSwitcherProps,
        closeAuthenticateModal: closeAuthenticateModal,
        showNameFields: showNameFields,
        showCustomAffiliateAside: showCustomAffiliateAside
      })
    })]
  });
};
export function useAuthDialog() {
  var context = useContext(AuthDialogContext);
  if (context === undefined) {
    throw new Error('useDialogContext must be used within an AuthDialogProvider');
  }
  return context;
}